import { socketOrigin } from "@/env";
import { clientId } from "@/lib/session/clientId";
import {
  AUTH_TIMEOUT_MS,
  CLOSE_CLIENT_RETRY,
  CLOSE_UNAUTHORIZED,
  decodeFrame,
  encodeFrame,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_MAX_MISSED,
  isFatalClose,
  PROTOCOL_VERSION,
  WS_PATH,
  type ClientEvents,
  type ServerEvents,
} from "@/lib/socket/protocol";

export type SocketStatus = "idle" | "connecting" | "authenticating" | "ready" | "closed";

export type SocketCredentials =
  | { kind: "user"; accessToken: string }
  | { kind: "guest"; displayName: string }
  | { kind: "vouched"; joinToken: string; accessToken?: string };

export type CredentialSource = (context: {
  rejected: boolean;
}) => SocketCredentials | Promise<SocketCredentials>;

function authPayload(credentials: SocketCredentials): ClientEvents["session:auth"] {
  const base = { protocol: PROTOCOL_VERSION, clientId: clientId() };
  if (credentials.kind === "user") {
    return { ...base, accessToken: credentials.accessToken };
  }
  if (credentials.kind === "vouched") {
    return credentials.accessToken === undefined
      ? { ...base, joinToken: credentials.joinToken }
      : { ...base, joinToken: credentials.joinToken, accessToken: credentials.accessToken };
  }
  return { ...base, guestName: credentials.displayName };
}

export type SocketHandlers = {
  onStatus?: (status: SocketStatus) => void;

  onReady?: (payload: ServerEvents["session:ready"], reconnected: boolean) => void;

  onClosed?: (code: number, reason: string) => void;
};

type Pending = {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export class SocketError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "SocketError";
    this.code = code;
  }
}

const REQUEST_TIMEOUT_MS = 20_000;
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 15_000] as const;

const WAKE_PROBE_MS = 4000;

const CLOSE_GRACE_MS = 3000;

type Listener = (payload: never) => void;

export class MeetSocket {
  #socket: WebSocket | null = null;
  #status: SocketStatus = "idle";
  #credentials: CredentialSource;
  #handlers: SocketHandlers;

  #nextRequestId = 1;
  #pending = new Map<number, Pending>();
  #listeners = new Map<string, Set<Listener>>();

  #outbox: string[] = [];

  #attempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  #authTimer: ReturnType<typeof setTimeout> | null = null;
  #probeTimer: ReturnType<typeof setTimeout> | null = null;
  #closeTimer: ReturnType<typeof setTimeout> | null = null;
  #missedBeats = 0;
  #heartbeatMs: number = HEARTBEAT_INTERVAL_MS;
  #everReady = false;
  #stopped = false;

  #started = false;

  #retrying = false;

  #goodbye: ServerEvents["session:closed"] | null = null;

  #rejected = false;

  #authRetried = false;

  #opening = 0;

  #serverOffsetMs = 0;
  #unwatch: (() => void)[] = [];

  constructor(credentials: CredentialSource, handlers: SocketHandlers = {}) {
    this.#credentials = credentials;
    this.#handlers = handlers;
    this.#watch();
  }

  get status(): SocketStatus {
    return this.#status;
  }

  get serverOffsetMs(): number {
    return this.#serverOffsetMs;
  }

  connect(): void {
    if (this.#stopped) return;
    this.#started = true;
    if (this.#socket !== null && this.#socket.readyState <= WebSocket.OPEN) return;
    this.#clearReconnect();
    this.#setStatus("connecting");
    void this.#open();
  }

  reconnectNow(): void {
    if (this.#stopped || !this.#started || this.#status === "ready") return;
    this.#attempt = 0;
    this.#clearReconnect();
    this.#drop();
    this.connect();
  }

  close(code = 1000, reason = "client closed"): void {
    this.#stopped = true;
    this.#teardownTimers();
    this.#unwatchAll();
    this.#failPending(new SocketError("closed", "The meeting connection was closed"));
    this.#outbox = [];
    const socket = this.#socket;
    this.#socket = null;
    socket?.close(code, reason);
    this.#setStatus("closed");
  }

  emit<E extends keyof ClientEvents>(event: E, payload: ClientEvents[E]): void {
    this.#send(encodeFrame({ t: event, d: payload }));
  }

  request<T, E extends keyof ClientEvents = keyof ClientEvents>(
    event: E,
    payload: ClientEvents[E],
  ): Promise<T> {
    const id = this.#nextRequestId++;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(id);
        reject(new SocketError("timeout", `The server did not answer ${event as string}`));
      }, REQUEST_TIMEOUT_MS);
      this.#pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });
      this.#send(encodeFrame({ t: event, d: payload, i: id }));
    });
  }

  on<E extends keyof ServerEvents>(
    event: E,
    listener: (payload: ServerEvents[E]) => void,
  ): () => void {
    let set = this.#listeners.get(event);
    if (set === undefined) {
      set = new Set();
      this.#listeners.set(event, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  async #open(): Promise<void> {
    const generation = ++this.#opening;
    let credentials: SocketCredentials;
    try {
      credentials = await this.#credentials({ rejected: this.#rejected });
    } catch {
      if (generation === this.#opening) this.#scheduleReconnect();
      return;
    }
    if (this.#stopped || generation !== this.#opening) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(`${socketOrigin()}${WS_PATH}`);
    } catch {
      this.#scheduleReconnect();
      return;
    }
    this.#socket = socket;
    this.#goodbye = null;

    const current = () => this.#socket === socket;

    socket.addEventListener("open", () => {
      if (!current()) {
        socket.close();
        return;
      }
      this.#setStatus("authenticating");
      this.#authTimer = setTimeout(() => {
        this.#retry("auth timed out");
      }, AUTH_TIMEOUT_MS);
      this.#sendRaw(encodeFrame({ t: "session:auth", d: authPayload(credentials) }));
    });

    socket.addEventListener("message", (event) => {
      if (!current() || typeof event.data !== "string") return;
      this.#onFrame(event.data);
    });

    socket.addEventListener("close", (event) => {
      if (!current()) return;
      this.#socket = null;
      this.#onDisconnected(event.code, event.reason);
    });

    socket.addEventListener("error", () => {});
  }

  #onDisconnected(closeCode: number, closeReason: string): void {
    this.#teardownConnectionTimers();
    this.#outbox = [];
    this.#failPending(new SocketError("disconnected", "The connection to the meeting was lost"));

    const goodbye = this.#goodbye;
    this.#goodbye = null;
    const code = goodbye?.code ?? closeCode;
    const reason = goodbye?.reason ?? closeReason;
    const fatal = goodbye?.fatal === true || isFatalClose(code);

    const forced = this.#retrying;
    this.#retrying = false;

    if (this.#stopped) {
      this.#setStatus("closed");
      return;
    }

    if (!forced && fatal) {
      if (code === CLOSE_UNAUTHORIZED && !this.#authRetried) {
        this.#authRetried = true;
        this.#rejected = true;
        this.#attempt = 0;
        this.#setStatus("connecting");
        this.#scheduleReconnect();
        return;
      }
      this.#setStatus("closed");
      this.#handlers.onClosed?.(code, reason);
      return;
    }

    this.#setStatus("connecting");
    this.#scheduleReconnect();
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer !== null || this.#stopped) return;
    const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)] ?? 15_000;
    this.#attempt += 1;

    const jittered = delay + Math.random() * delay * 0.3;
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.connect();
    }, jittered);
  }

  #clearReconnect(): void {
    if (this.#reconnectTimer !== null) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
  }

  #retry(reason: string): void {
    const socket = this.#socket;
    if (socket === null || this.#retrying) return;
    this.#retrying = true;
    socket.close(CLOSE_CLIENT_RETRY, reason);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      if (this.#socket !== socket) return;
      this.#socket = null;
      this.#onDisconnected(CLOSE_CLIENT_RETRY, reason);
    }, CLOSE_GRACE_MS);
  }

  #drop(): void {
    const socket = this.#socket;
    if (socket === null) return;
    this.#socket = null;
    this.#teardownConnectionTimers();
    this.#retrying = false;
    this.#outbox = [];
    this.#failPending(new SocketError("disconnected", "The connection to the meeting was lost"));
    socket.close(CLOSE_CLIENT_RETRY, "retrying now");
  }

  #watch(): void {
    const wake = () => {
      this.#onWake();
    };
    const visible = () => {
      if (document.visibilityState === "visible") this.#onWake();
    };
    globalThis.addEventListener("online", wake);
    globalThis.addEventListener("pageshow", wake);
    document.addEventListener("visibilitychange", visible);
    this.#unwatch.push(() => {
      globalThis.removeEventListener("online", wake);
      globalThis.removeEventListener("pageshow", wake);
      document.removeEventListener("visibilitychange", visible);
    });
  }

  #unwatchAll(): void {
    for (const stop of this.#unwatch) stop();
    this.#unwatch = [];
  }

  #onWake(): void {
    if (this.#stopped || !this.#started) return;

    if (this.#status === "ready") {
      this.#probe();
      return;
    }

    this.#attempt = 0;
    this.#clearReconnect();
    this.connect();
  }

  #probe(): void {
    if (this.#probeTimer !== null || this.#socket === null) return;
    this.#missedBeats += 1;
    this.#sendRaw(encodeFrame({ t: "session:ping", d: { ts: Date.now() } }));
    this.#probeTimer = setTimeout(() => {
      this.#probeTimer = null;
      if (this.#missedBeats > 0) this.#retry("no answer after waking");
    }, WAKE_PROBE_MS);
  }

  #setStatus(status: SocketStatus): void {
    if (this.#status === status) return;
    this.#status = status;
    this.#handlers.onStatus?.(status);
  }

  #send(frame: string): void {
    if (this.#status === "ready" && this.#socket?.readyState === WebSocket.OPEN) {
      this.#socket.send(frame);
      return;
    }
    this.#outbox.push(frame);
  }

  #sendRaw(frame: string): void {
    if (this.#socket?.readyState === WebSocket.OPEN) this.#socket.send(frame);
  }

  #flush(): void {
    const queued = this.#outbox;
    this.#outbox = [];
    for (const frame of queued) this.#sendRaw(frame);
  }

  #onFrame(raw: string): void {
    const frame = decodeFrame(raw);
    if (frame === null) return;

    if (frame.i !== undefined) {
      const pending = this.#pending.get(frame.i);
      if (pending === undefined) return;
      this.#pending.delete(frame.i);
      clearTimeout(pending.timer);
      if (frame.e === true) {
        const detail = (frame.d ?? {}) as { code?: string; message?: string };
        pending.reject(
          new SocketError(detail.code ?? "error", detail.message ?? "The request was refused"),
        );
      } else {
        pending.resolve(frame.d);
      }
      return;
    }

    if (frame.t === "session:ready") {
      const payload = frame.d as ServerEvents["session:ready"];
      if (this.#authTimer !== null) clearTimeout(this.#authTimer);
      this.#authTimer = null;
      this.#attempt = 0;
      this.#rejected = false;
      this.#authRetried = false;
      this.#readClock(payload);
      this.#setStatus("ready");
      this.#flush();
      this.#startHeartbeat();
      const reconnected = this.#everReady;
      this.#everReady = true;
      this.#handlers.onReady?.(payload, reconnected);
      return;
    }

    this.#missedBeats = 0;
    if (frame.t === "session:pong") return;

    if (frame.t === "session:closed") {
      this.#goodbye = frame.d as ServerEvents["session:closed"];
      return;
    }

    const listeners = this.#listeners.get(frame.t);
    if (listeners === undefined) return;
    for (const listener of listeners) (listener as (payload: unknown) => void)(frame.d);
  }

  #readClock(payload: ServerEvents["session:ready"]): void {
    const server = Date.parse(payload.serverTime ?? "");
    if (!Number.isNaN(server)) this.#serverOffsetMs = server - Date.now();

    if (typeof payload.heartbeatIntervalMs === "number" && payload.heartbeatIntervalMs >= 1000) {
      this.#heartbeatMs = payload.heartbeatIntervalMs;
    }
  }

  #startHeartbeat(): void {
    if (this.#heartbeatTimer !== null) clearInterval(this.#heartbeatTimer);
    this.#missedBeats = 0;
    this.#heartbeatTimer = setInterval(() => {
      if (this.#missedBeats >= HEARTBEAT_MAX_MISSED) {
        this.#retry("heartbeat lost");
        return;
      }
      this.#missedBeats += 1;
      this.#sendRaw(encodeFrame({ t: "session:ping", d: { ts: Date.now() } }));
    }, this.#heartbeatMs);
  }

  #teardownConnectionTimers(): void {
    if (this.#heartbeatTimer !== null) clearInterval(this.#heartbeatTimer);
    if (this.#authTimer !== null) clearTimeout(this.#authTimer);
    if (this.#probeTimer !== null) clearTimeout(this.#probeTimer);
    if (this.#closeTimer !== null) clearTimeout(this.#closeTimer);
    this.#heartbeatTimer = null;
    this.#authTimer = null;
    this.#probeTimer = null;
    this.#closeTimer = null;
  }

  #teardownTimers(): void {
    this.#teardownConnectionTimers();
    this.#clearReconnect();
  }

  #failPending(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timer);
      pending.reject(error);
    }
    this.#pending.clear();
  }
}
